import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import FloatingShapes from './FloatingShapes';
import {
  Server,
  Cloud,
  Shield,
  Code,
  Cpu,
  Globe,
  Settings,
  Bot,
  Brain,
  ShoppingCart,
  QrCode,
  Utensils,
  Target,
  Lightbulb,
  Users,
  Award,
  CheckCircle,
  ChevronUp,
} from 'lucide-react';

const icons = [
  { emoji: '💻', from: { x: '5vw', y: '20vh' }, to: { x: '15vw', y: '10vh' }, delay: 0 },
  { emoji: '☁️', from: { x: '80vw', y: '30vh' }, to: { x: '70vw', y: '20vh' }, delay: 0.4 },
  { emoji: '🔒', from: { x: '10vw', y: '70vh' }, to: { x: '20vw', y: '60vh' }, delay: 0.8 },
  { emoji: '⚙️', from: { x: '60vw', y: '80vh' }, to: { x: '50vw', y: '70vh' }, delay: 1.2 },
  { emoji: '🤖', from: { x: '40vw', y: '50vh' }, to: { x: '60vw', y: '45vh' }, delay: 1.6 },
  { emoji: '🌐', from: { x: '30vw', y: '10vh' }, to: { x: '40vw', y: '20vh' }, delay: 2 },
];

const techIcons = [
  { Icon: Server,   from: { x: '10vw', y: '15vh' }, to: { x: '20vw', y: '10vh' }, delay: 0   },
  { Icon: Cloud,    from: { x: '85vw', y: '25vh' }, to: { x: '75vw', y: '20vh' }, delay: 0.3 },
  { Icon: Shield,   from: { x: '5vw',  y: '75vh' }, to: { x: '15vw', y: '65vh' }, delay: 0.6 },
  { Icon: Code,     from: { x: '70vw', y: '85vh' }, to: { x: '60vw', y: '75vh' }, delay: 0.9 },
  { Icon: Cpu,      from: { x: '45vw', y: '55vh' }, to: { x: '55vw', y: '50vh' }, delay: 1.2 },
  { Icon: Globe,    from: { x: '25vw', y: '15vh' }, to: { x: '35vw', y: '25vh' }, delay: 1.5 },
  { Icon: Settings, from: { x: '75vw', y: '70vh' }, to: { x: '65vw', y: '60vh' }, delay: 1.8 },
  { Icon: Bot,      from: { x: '30vw', y: '40vh' }, to: { x: '40vw', y: '30vh' }, delay: 2.1 },
];

const teamMembers = [
  {
    name: 'Karan Makwana',
    role: 'Co-Founder & Full Stack Developer',
    image: '/images/my photo.jpg',
    skills: ['React', 'Node.js', '.NET', 'MongoDB'],
    linkedin: '#',
  },
  {
    name: 'Om Makwana',
    role: 'Co-Founder & Solution Architect',
    image: 'https://omkaranfurniture.com/Owners/WhatsApp%20Image%202024-12-28%20at%2020.36.29_772a2094.jpg',
    skills: ['React Native', 'ASP.NET', 'MySQL', 'DevOps'],
    linkedin: '#',
  },
];
  const hoverVariants = {
    hover: { scale: 1.05, transition: { duration: 0.3 } },
    tap: { scale: 0.98 }
  };

const About = () => {
  const sections = useRef([]);
  const [showTop, setShowTop] = useState(false);


  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    sections.current.forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInScale');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      observers.push(observer);
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  // Native touch scroll is used — no override needed

  return (
    <div className="min-h-screen bg-gray-950">

      {/* ── HERO (unchanged) ── */}
      <section
        ref={(el) => (sections.current[0] = el)}
        className="relative min-h-[850px] text-white flex items-center justify-center text-center overflow-hidden opacity-0 py-20"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-blue-900/90 z-0" />

        {icons.map((icon, index) => (
          <motion.div
            key={index}
            className="absolute text-3xl sm:text-4xl opacity-40 select-none z-0"
            style={{ left: icon.from.x, top: icon.from.y }}
            animate={{ left: icon.to.x, top: icon.to.y, opacity: [0.4, 0.8, 0.4], rotate: [0, 10, -10, 0] }}
            transition={{ duration: 6, delay: icon.delay, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          >
            {icon.emoji}
          </motion.div>
        ))}

        {techIcons.map(({ Icon, from, to, delay }, index) => (
          <motion.div
            key={index}
            className="absolute text-white opacity-30 z-0"
            style={{ left: from.x, top: from.y }}
            animate={{ left: to.x, top: to.y, opacity: [0.3, 0.6, 0.3], rotate: [0, 15, -15, 0] }}
            transition={{ repeat: Infinity, repeatType: 'reverse', duration: 6, delay, ease: 'easeInOut' }}
          >
            <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
          </motion.div>
        ))}

        <div className="relative z-10 px-6 sm:px-10 lg:px-24 max-w-5xl">
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-8 text-shadow-lg text-white"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          >
            About CmCoreTech
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-100 mb-10 leading-relaxed text-shadow-md"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          >
            Discover our mission to drive digital transformation through innovative software, cloud, and cybersecurity solutions.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-6 mb-10"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a href="contact" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300">
              Get in Touch
            </a>
            <a href="solutions" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300">
              Explore Services
            </a>
          </motion.div>
        
        </div>
      </section>

      {/* ══════════════════════════════════════════════
           ABOUT US — deep blue, story + mission/values
         ══════════════════════════════════════════════ */}
      <section
        id="about"
        ref={(el) => (sections.current[1] = el)}
        className="relative py-28 overflow-hidden opacity-0"
        style={{ background: 'linear-gradient(160deg, #060d1f 0%, #0b1535 40%, #060e22 100%)' }}
      >
        <FloatingShapes />

        {/* Top + bottom border lines */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

        {/* Glow blobs */}
        <motion.div className="absolute top-[-120px] left-[-120px] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.09) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-[-100px] right-[-100px] w-[450px] h-[450px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 3 }} />

        <div className="relative z-10 container mx-auto px-6 lg:px-20">

          {/* ── Section label + heading ── */}
          <div className="text-center mb-20">
            <motion.span
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.14)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            >Who We Are</motion.span>
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            >
              Empowering <span className="text-blue-400">Digital Innovation</span>
            </motion.h2>
            <motion.p
              className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
            >
              At <span className="text-blue-300 font-semibold">CmCoreTech</span>, we craft modern digital solutions
              that help businesses grow faster, operate smarter, and reach further.
            </motion.p>
          </div>

          {/* ── Two-column: story left, image right ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">

            {/* Left — feature list */}
            <motion.div className="space-y-5"
              initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            >
              {[
                { Icon: Cpu,          color: 'blue',   title: 'Custom Software Development', desc: 'Robust, scalable apps built for performance, growth, and real user needs — web, mobile, and desktop.' },
                { Icon: ShoppingCart, color: 'purple', title: 'eCommerce Solutions',          desc: 'Full-featured online stores with secure payments, smart inventory, and conversion-focused UX.' },
                { Icon: QrCode,       color: 'cyan',   title: 'QR-Based Solutions',           desc: 'Smart QR systems for digital menus, access control, tracking, and instant customer engagement.' },
                { Icon: Utensils,     color: 'orange', title: 'Restaurant Technology',        desc: 'End-to-end dining tech — table ordering, kitchen displays, billing automation, and loyalty tools.' },
              ].map(({ Icon, color, title, desc }, i) => {
                const cm = { blue: ['rgba(37,99,235,0.12)','rgba(59,130,246,0.3)','#60a5fa'], purple: ['rgba(124,58,237,0.12)','rgba(139,92,246,0.3)','#c084fc'], cyan: ['rgba(6,182,212,0.12)','rgba(6,182,212,0.3)','#22d3ee'], orange: ['rgba(234,88,12,0.12)','rgba(234,88,12,0.3)','#fb923c'] };
                const [bg, border, ic] = cm[color];
                return (
                  <motion.div key={i}
                    className="flex items-start gap-4 p-4 rounded-2xl"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.1 }}
                    whileHover={{ background: bg, borderColor: border, transition: { duration: 0.2 } }}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg, border: `1px solid ${border}` }}>
                      <Icon className="w-5 h-5" style={{ color: ic }} />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-base mb-1">{title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Right — image with floating stat badges */}
            <motion.div className="relative flex justify-center"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            >
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-25 pointer-events-none" style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }} />
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=700&q=80"
                  alt="CmCoreTech Team at work"
                  className="relative z-10 rounded-3xl w-full object-cover shadow-2xl"
                  style={{ border: '1px solid rgba(59,130,246,0.2)', aspectRatio: '4/3' }}
                />
                {/* Stat badge — bottom left */}
                <motion.div className="absolute -bottom-6 -left-6 z-20 px-5 py-3 rounded-2xl shadow-xl"
                  style={{ background: 'rgba(8,14,35,0.97)', border: '1px solid rgba(59,130,246,0.35)', backdropFilter: 'blur(16px)' }}
                  animate={{ y: [0, -7, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <div className="text-2xl font-extrabold text-blue-400">50+</div>
                  <div className="text-xs text-gray-400 font-medium mt-0.5">Projects Delivered</div>
                </motion.div>
                {/* Stat badge — top right */}
                <motion.div className="absolute -top-6 -right-6 z-20 px-5 py-3 rounded-2xl shadow-xl"
                  style={{ background: 'rgba(8,14,35,0.97)', border: '1px solid rgba(139,92,246,0.35)', backdropFilter: 'blur(16px)' }}
                  animate={{ y: [0, 7, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
                >
                  <div className="text-2xl font-extrabold text-purple-400">98%</div>
                  <div className="text-xs text-gray-400 font-medium mt-0.5">Client Satisfaction</div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* ── Mission / Vision / Values cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: Target,    label: 'Our Mission', color: 'blue',   text: 'To deliver cutting-edge technology solutions that empower businesses to scale, automate, and compete in the digital age.' },
              { Icon: Lightbulb, label: 'Our Vision',  color: 'purple', text: 'To become the most trusted IT partner for startups and enterprises across India and beyond.' },
              { Icon: Award,     label: 'Our Values',  color: 'cyan',   text: 'Transparency, innovation, and long-term client success drive every project we take on — no shortcuts, ever.' },
            ].map(({ Icon, label, color, text }, i) => {
              const cm = { blue: ['rgba(37,99,235,0.1)','rgba(59,130,246,0.25)','#60a5fa','rgba(59,130,246,0.5)'], purple: ['rgba(124,58,237,0.1)','rgba(139,92,246,0.25)','#c084fc','rgba(139,92,246,0.5)'], cyan: ['rgba(6,182,212,0.1)','rgba(6,182,212,0.25)','#22d3ee','rgba(6,182,212,0.5)'] };
              const [bg, border, ic, hb] = cm[color];
              return (
                <motion.div key={i}
                  className="p-7 rounded-2xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border}` }}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}
                  whileHover={{ borderColor: hb, background: bg, transition: { duration: 0.2 } }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: bg, border: `1px solid ${border}` }}>
                    <Icon className="w-6 h-6" style={{ color: ic }} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{label}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{text}</p>
                </motion.div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════
           TEAM SECTION — lighter dark, full contrast
         ══════════════════════════════════════════════ */}
         <section
        className="bg-gray-950 text-white py-20 opacity-0 relative interactive-section"
        ref={(el) => (sections.current[7] = el)}
      >
          <FloatingShapes />

        <div className="container mx-auto px-6">
          <motion.h2
            className="text-4xl font-bold text-center mb-14 cursor-pointer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            whileHover={{ color: '#93c5fd' }}
          >
            Meet Our <span className="text-blue-400">Team</span>
          </motion.h2>
          <div className="flex flex-col md:flex-row gap-10 justify-center items-center">
            {teamMembers.map((member, i) => (
              <motion.div
                key={i}
                className="bg-gray-900 p-6 rounded-xl shadow-lg text-center max-w-xs w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                variants={hoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-400">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold">{member.name}</h3>
                <p className="text-gray-400">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 text-gray-300 py-6 text-center">
        <p>© 2025 CmCoreTech. All rights reserved.</p>
        <p className="mt-2">Contact: cmcoretech@gmail.com | +91 87806 43219</p>
      </footer>

      {/* ══ BACK TO TOP ══ */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showTop ? 1 : 0, scale: showTop ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.15, boxShadow: '0 0 20px rgba(99,102,241,0.6)' }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5 text-white" />
      </motion.button>

    </div>
  );
};

export default About;
